import {
  Alert,
  Button,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { isApiError, leagueApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { formatDateTime } from "@bw/shared/utils";
import { useRef } from "react";
import {
  Link as RouterLink,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Page } from "../../../common/components/Page";
import { DetailHeader, ShareButton } from "../../../common/components/DetailHeader";
import { DivisionNav, DivisionTitle } from "./LeagueStandingsPage";
import { queryKeys } from "../../../common/query/queryClient";
import { useMyProfile } from "../../../common/query/useMyProfile";
import { LeagueOfferingFacts } from "../components/LeagueOfferingFacts";
import { useRegisterForDivision } from "../hooks/useRegisterForDivision";
import { mapRegisterError } from "../utils/mapRegisterError";
import { useSessionParticipation } from "../hooks/useSessionParticipation";
import { SessionParticipationActions } from "../components/SessionParticipationActions";
import { readSearchReturn } from "../../../common/searchNavigation";

export function LeaguePublicDivisionPage() {
  const { divisionId = "" } = useParams<{ divisionId: string }>();
  const { user } = useAuth();
  return (
    <PublicDivisionBody
      key={`${user?.uid ?? "anonymous"}:${divisionId}`}
      divisionId={divisionId}
    />
  );
}

function PublicDivisionBody({ divisionId }: { divisionId: string }) {
  const location = useLocation();
  const searchReturn = readSearchReturn(location.search);
  const [params, setParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const profile = useMyProfile(Boolean(user));
  const detail = useQuery({
    queryKey: [...queryKeys.leagues.all, "public-division", divisionId],
    queryFn: () => leagueApi.getPublicDivision(divisionId),
    enabled: Boolean(divisionId),
  });
  const rawPage = Number(params.get("session_page"));
  const sessionId = params.get("session_id") || undefined;
  const sessionPage =
    !sessionId && Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const seasonId = detail.data?.season_id;
  const sessions = useQuery({
    queryKey: [
      ...queryKeys.leagues.all,
      "public-sessions",
      seasonId,
      sessionId,
      sessionPage,
    ],
    queryFn: () =>
      leagueApi.listPublicSessions(seasonId!, {
        limit: 20,
        offset: (sessionPage - 1) * 20,
        ...(sessionId ? { session_id: sessionId } : {}),
      }),
    enabled: Boolean(seasonId),
  });
  const participation = useSessionParticipation(
    sessions.data?.items.map((session) => session.session_id) ?? [],
    Boolean(user && profile.isSuccess && sessions.isSuccess),
  );
  const mine = useQuery({
    queryKey: [...queryKeys.me.leagueRegistrations(), user?.uid, { divisionId }],
    queryFn: () => leagueApi.listMyLeagueRegistrations({ division_id: divisionId, limit: 1, offset: 0 }),
    enabled: Boolean(user && profile.data),
  });
  const registration = useRegisterForDivision();
  const submitLock = useRef(false);
  const submittedBy = useRef<string | null>(null);
  const myRegistration = user
    ? mine.data?.items.find((item) => item.division_id === divisionId)
    : undefined;
  const registered = myRegistration?.status === "registered";
  const currentAttempt = Boolean(
    user &&
      submittedBy.current === user.uid &&
      registration.variables === divisionId,
  );
  const returnUrl = encodeURIComponent(location.pathname + location.search);
  async function register() {
    if (
      !user ||
      !profile.data ||
      !mine.isSuccess ||
      mine.isFetching ||
      detail.data?.registration_state !== "open" ||
      registered ||
      submitLock.current
    )
      return;
    submitLock.current = true;
    submittedBy.current = user.uid;
    try {
      await registration.mutateAsync(divisionId);
    } catch {
      /* Source error shown below; no optimistic registration. */
    } finally {
      submitLock.current = false;
    }
  }
  if (detail.isLoading)
    return (
      <Page title="League division">
        <Typography role="status">Loading division…</Typography>
      </Page>
    );
  if (detail.isError || !detail.data)
    return (
      <Page title="League division">
        <Alert
          severity="error"
          action={<Button onClick={() => void detail.refetch()}>Retry</Button>}
        >
          This division is unavailable.
        </Alert>
      </Page>
    );
  const offering = detail.data;
  return (
    <Page narrow>
      <Stack spacing={2}>
        <DetailHeader
          title={
            <DivisionTitle
              league={offering.league_name}
              division={offering.division_name}
            />
          }
          subtitle={offering.season_name}
          back={{
            to:
              searchReturn ??
              `/compete/leagues?facility_id=${encodeURIComponent(offering.facility_id)}`,
            label: searchReturn ? "Back to search" : "Back to leagues",
          }}
          actions={
            <ShareButton
              title={[offering.league_name, offering.division_name]
                .filter(Boolean)
                .join(" · ")}
            />
          }
        />
        {user ? <DivisionNav divisionId={divisionId} active="sessions" /> : null}
        <LeagueOfferingFacts offering={offering} />
        {offering.league_description ? (
          <Typography>{offering.league_description}</Typography>
        ) : null}
        {authLoading ? (
          <Typography role="status">Loading account…</Typography>
        ) : !user ? (
          <Button
            component={RouterLink}
            to={`/login?returnUrl=${returnUrl}`}
            variant="contained"
          >
            Sign in to register
          </Button>
        ) : profile.isLoading ? (
          <Typography role="status">Loading your profile…</Typography>
        ) : profile.isError ? (
          isApiError(profile.error) && profile.error.status === 404 ? (
            <Button
              component={RouterLink}
              to={`/onboarding?returnUrl=${returnUrl}`}
            >
              Complete your profile to register
            </Button>
          ) : (
            <Alert
              severity="error"
              action={
                <Button onClick={() => void profile.refetch()}>Retry</Button>
              }
            >
              Could not verify your account for registration.
            </Alert>
          )
        ) : registered ? (
          <Alert severity="success">You’re registered.</Alert>
        ) : myRegistration?.status === "withdrawn" ? (
          <Typography role="status">
            Your registration was withdrawn.
          </Typography>
        ) : mine.isError ? (
          <Alert
            severity="error"
            action={<Button onClick={() => void mine.refetch()}>Retry</Button>}
          >
            Could not load your registrations.
          </Alert>
        ) : (
          <Button
            variant="contained"
            disabled={
              offering.registration_state !== "open" ||
              !mine.isSuccess ||
              mine.isFetching ||
              registration.isPending
            }
            onClick={() => void register()}
          >
            Register
          </Button>
        )}
        {currentAttempt && registration.isError && !myRegistration ? (
          <Alert severity="error">{mapRegisterError(registration.error)}</Alert>
        ) : null}
        <Stack component="section" aria-label="Season schedule" spacing={1.5}>
          <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 700 }}>
            Season schedule
          </Typography>
          {participation.enabled && participation.query.isLoading ? (
            <Typography role="status">Loading your session status…</Typography>
          ) : null}
          {participation.enabled && participation.query.isError ? (
            <Alert
              severity="error"
              action={
                <Button onClick={() => void participation.query.refetch()}>
                  Retry session status
                </Button>
              }
            >
              Could not load your session status.
            </Alert>
          ) : null}
          {sessionId ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography>Selected session</Typography>
              <Button onClick={() => {
                const next = new URLSearchParams(params);
                next.delete("session_id");
                next.delete("session_page");
                setParams(next);
              }}>All sessions</Button>
            </Stack>
          ) : null}
          {sessions.isLoading ? (
            <Typography role="status">Loading sessions…</Typography>
          ) : sessions.isError ? (
            <Alert
              severity="error"
              action={
                <Button onClick={() => void sessions.refetch()}>Retry</Button>
              }
            >
              Could not load the schedule.
            </Alert>
          ) : !sessions.data?.items.length ? (
            <Typography>{sessionId ? "This session is not available in this season." : "No sessions scheduled."}</Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary">
                Times shown in your device’s time zone.
              </Typography>
              <List disablePadding>
                {sessions.data.items.map((session) => (
                  <ListItem key={session.session_id} divider disableGutters>
                    <Stack spacing={0.5}>
                      <Typography sx={{ fontWeight: 600 }}>
                        Session {session.session_number}
                      </Typography>
                      <Typography>
                        {formatDateTime(session.start_time)} –{" "}
                        {formatDateTime(session.end_time)}
                      </Typography>
                      <Typography variant="body2">
                        {session.status.replaceAll("_", " ")}
                      </Typography>
                      {participation.enabled &&
                      participation.query.isSuccess ? (
                        <SessionParticipationActions
                          state={participation.query.data.find(
                            (item) => item.session_id === session.session_id,
                          )}
                          number={session.session_number}
                          pending={
                            participation.query.isFetching ||
                            participation.pending.has(session.session_id)
                          }
                          error={participation.errors[session.session_id]}
                          onJoin={() =>
                            void participation.update(
                              session.session_id,
                              "join",
                            )
                          }
                          onCancel={() =>
                            void participation.update(
                              session.session_id,
                              "cancel",
                            )
                          }
                        />
                      ) : null}
                    </Stack>
                  </ListItem>
                ))}
              </List>
            </>
          )}
          {sessions.data && (sessionPage > 1 || sessions.data.has_more) ? (
            <Stack direction="row" spacing={1}>
              <Button
                disabled={sessionPage === 1}
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.set("session_page", String(sessionPage - 1));
                  setParams(next);
                }}
              >
                Previous sessions
              </Button>
              <Button
                disabled={!sessions.data.has_more}
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.set("session_page", String(sessionPage + 1));
                  setParams(next);
                }}
              >
                Next sessions
              </Button>
            </Stack>
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button
            component={RouterLink}
            to={`/compete/leagues/divisions/${divisionId}/standings`}
          >
            Division standings
          </Button>
          {user ? (
            <Button component={RouterLink} to="/compete/mine?type=league">
              My league registrations
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Page>
  );
}
